import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import cn from 'classnames';

import { useSelector } from 'react-redux';
import { selectUserToken } from '../redux/features/user/userSelectors';
import { useGetRemindersQuery } from '../redux/services/reminders/remindersApi';

import { TabBar } from '../components/ui/TabBar';
import { Container } from '../layouts/Container';
import { ErrorMsg } from '../components/ui/ErrorMsg';
import { Loader } from '../components/ui/Loader';
import { LoaderProgress } from '../components/ui/LoaderProgress';
import { RemindersList } from '../components/ui/remindersLists/RemindersList';

import { errorMessages, tabs } from '../data/constants';
import { createGroupedHashMap } from '../utils/utils';

const urgencyFilters = [
  {
    key: 'Urgent',
    title: 'Urgent',
    activeClasses: 'bg-redColor border-redColor text-white',
    idleClasses: 'border-redColor text-redColor hover:bg-red-50',
  },
  {
    key: 'Important',
    title: 'Important',
    activeClasses: 'bg-orangeColor border-orangeColor text-white',
    idleClasses: 'border-orangeColor text-orangeColor hover:bg-orange-50',
  },
];

const getGroupKey = reminder =>
  `${reminder?.reminder_subtype?.value}:${
    reminder?.realty?.id ??
    (reminder?.client_request?.id
      ? `request-${reminder.client_request.id}`
      : `reminder-${reminder?.id}`)
  }`;

// date comes as d-m-Y
const toSortableDate = date => date?.split('-').reverse().join('') ?? '';

const compareByDateDesc = (a, b) =>
  toSortableDate(b?.date).localeCompare(toSortableDate(a?.date)) ||
  (b?.id ?? 0) - (a?.id ?? 0);

const matchesSearch = (reminder, query, queryDigits) => {
  const { realty, contacts, client, client_request } = reminder;
  const people = [...(contacts || []), client];
  const haystack = [
    realty?.id,
    realty?.street_number,
    realty?.street,
    realty?.postal_code,
    realty?.city,
    client_request?.id,
    client_request?.realty_id,
    reminder?.subject,
    reminder?.date,
    ...people.flatMap(person => [
      person?.first_name,
      person?.last_name,
      person?.email,
      person?.phone,
    ]),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (haystack.includes(query)) return true;

  return (
    queryDigits.length >= 3 &&
    people.some(person =>
      person?.phone?.replace(/\D/g, '').includes(queryDigits)
    )
  );
};

export const Reminders = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const type = searchParams.get('type') || '0';
  const urgency = searchParams.get('urgency');
  const isFinished = searchParams.get('status') === 'finished';
  const [search, setSearch] = useState('');

  const userToken = useSelector(selectUserToken);
  const {
    currentData: reminders = {},
    isLoading,
    isFetching,
    error,
  } = useGetRemindersQuery({ token: userToken, finished: isFinished });
  const isListLoading = isFetching && !reminders.data;

  const groups = useMemo(() => {
    const groupsMap = createGroupedHashMap(reminders?.data || [], getGroupKey);

    return Object.entries(groupsMap)
      .map(([key, items]) => {
        const sorted = [...items].sort(compareByDateDesc);
        return { key, head: sorted[0], reminders: sorted };
      })
      .sort((a, b) => compareByDateDesc(a.head, b.head));
  }, [reminders]);

  const counters = useMemo(() => {
    const result = {};

    tabs.forEach(tab => {
      result[tab.value] = { Urgent: 0, Important: 0, All: 0 };
    });

    groups.forEach(({ head }) => {
      const tabCounters = result[head?.reminder_subtype?.value];
      if (!tabCounters) return;

      tabCounters.All++;
      const urgencyTitle = head?.reminder_type?.title;
      if (urgencyTitle in tabCounters) tabCounters[urgencyTitle]++;
    });

    return result;
  }, [groups]);

  const visibleGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    const queryDigits = query.replace(/\D/g, '');

    return groups.filter(({ head, reminders: items }) => {
      if (head?.reminder_subtype?.value !== Number(type)) return false;
      if (urgency && head?.reminder_type?.title !== urgency) return false;
      if (!query) return true;

      return items.some(reminder =>
        matchesSearch(reminder, query, queryDigits)
      );
    });
  }, [groups, type, urgency, search]);

  const updateParams = next => {
    const params = {
      type,
      urgency,
      status: isFinished ? 'finished' : null,
      ...next,
    };

    setSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v)),
      { replace: true }
    );
  };

  const handleTabClick = value => {
    updateParams({ type: String(value), urgency: null });
  };

  const handleCounterClick = (value, counterKey) => {
    const isToggleOff =
      counterKey === 'All' ||
      (String(value) === type && counterKey === urgency);

    updateParams({
      type: String(value),
      urgency: isToggleOff ? null : counterKey,
    });
  };

  const handleStatusClick = finished => {
    updateParams({ status: finished ? 'finished' : null });
  };

  return (
    <Container>
      <div className="flex flex-col items-start gap-4">
        <TabBar
          activeTab={type}
          activeUrgency={urgency}
          counters={counters}
          handleTabClick={handleTabClick}
          handleCounterClick={handleCounterClick}
          isLoading={isLoading}
        />

        {!isLoading && (
          <div className="flex flex-wrap items-center gap-2">
            {[
              { title: 'Active', finished: false },
              { title: 'Finished', finished: true },
            ].map(({ title, finished }) => (
              <button
                key={title}
                type="button"
                onClick={() => handleStatusClick(finished)}
                className={cn(
                  'py-1.5 px-3 text-sm text-blackColor font-medium rounded-lg border border-gray-200 hover:bg-gray-100 transition',
                  { 'bg-gray-100': finished === isFinished }
                )}
              >
                {title}
              </button>
            ))}

            <span className="mx-1 w-px h-6 bg-gray-200" />

            {urgencyFilters.map(({ key, title, activeClasses, idleClasses }) => {
              const isActive = urgency === key;

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleCounterClick(type, key)}
                  className={cn(
                    'py-1.5 px-3 text-sm font-medium rounded-lg border transition',
                    isActive ? activeClasses : idleClasses
                  )}
                >
                  {title} {counters[type]?.[key] || 0}
                </button>
              );
            })}
          </div>
        )}

        {!isLoading && (
          <input
            type="search"
            placeholder="Search by ID, address, name, phone, email, subject"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="px-3 py-2 w-full max-w-md text-sm border border-gray-200 rounded outline-none focus:border-gray-400"
          />
        )}

        {isFetching && !isListLoading && <LoaderProgress />}

        {isListLoading && (
          <div className="flex justify-center items-center h-40 w-full">
            <Loader
              width={30}
              height={30}
            />
          </div>
        )}

        {visibleGroups.length > 0 && (
          <RemindersList
            type={type}
            groups={visibleGroups}
          />
        )}

        {!isListLoading && !error && visibleGroups.length < 1 && (
          <div className="flex justify-center items-center h-40 w-full">
            <span className="text-blackColor font-medium text-ml">
              No reminders
            </span>
          </div>
        )}

        {error && !isLoading && <ErrorMsg message={errorMessages.reminders} />}
      </div>
    </Container>
  );
};
